import { action } from '@ember/object';
import { cached } from '@glimmer/tracking';

import DOMPurify from 'dompurify';

import type { PopupData } from 'webapp-types';
import {
	PopupContextSwitchAction as Action,
	PopupContextSwitchModelType as ModelType,
} from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Block from 'webapp/models-custom/block';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';
import ReportSlide from 'webapp/models-custom/report-slide';

interface ModelProperties {
	name: string;
	type: string;
	isArray?: boolean;
}

export default class CommonPopupContextSwitchComponent extends PopupInnerModalComponent<PopupData.Common.ContextSwitch> {
	get isDuplicateAction(): boolean {
		return this.actionType === Action.DUPLICATE;
	}

	get isAddToAction(): boolean {
		return this.actionType === Action.ADD_TO;
	}

	get actionType(): Action {
		return this.popupData.actionType;
	}

	@cached
	get sourceItemData(): ModelProperties {
		return this.getModelProperties(this.popupData.sourceItem);
	}

	@cached
	get targetItemData(): ModelProperties {
		return this.getModelProperties(this.popupData.targetItem);
	}

	get actionTypeName(): string {
		if (this.isDuplicateAction) {
			return 'duplicated';
		} else if (this.isAddToAction) {
			return 'added';
		}

		return '';
	}

	get stayButtonText(): string {
		if (this.isDuplicateAction) {
			return 'Stay on original';
		} else {
			return 'Ok, stay here';
		}
	}

	get buttonText(): string {
		return `Switch to the ${this.targetItemData.type}`;
	}

	get formattedText() {
		const sourceItemData = this.sourceItemData;

		let text = 'The';

		if (this.isAddToAction) {
			text += ' selected';
		}

		if (!sourceItemData.isArray && sourceItemData.name) {
			text += ` <strong>${sourceItemData.name}</strong>`;
		}

		text += ` ${sourceItemData.type}${sourceItemData.isArray ? 's have' : ' has'} been successfully ${this.actionTypeName}`;

		if (this.isAddToAction) {
			const targetItemData = this.targetItemData;

			text += ' to the ';

			if (targetItemData.name) {
				text += `<strong>${targetItemData.name}</strong> `;
			}

			text += targetItemData.type;
		}

		return DOMPurify.sanitize(text, {
			ALLOWED_TAGS: ['strong'],
		});
	}

	private getModelProperties(
		model?: PopupData.Common.ContextSwitch['sourceItem'],
	): ModelProperties {
		let name = '',
			type = '';

		const isArray = Array.isArray(model),
			item = isArray ? model[0] : model;

		if (item instanceof Board) {
			name = item.isAccountTemplate ? 'Template' : item.name;
			type = ModelType.BOARD;
		} else if (item instanceof BoardLoop) {
			name = item.name;
			type = ModelType.LOOP;
		} else if (item instanceof Block) {
			name = item.name;
			type = ModelType.BLOCK;
		} else if (item instanceof Report) {
			name = item.title;
			type = ModelType.REPORT;
		} else if (item instanceof ReportSlide) {
			name = item.title;
			type = ModelType.SLIDE;
		} else if (item) {
			type = item.toString();
		}

		return {
			name,
			type,
			isArray,
		};
	}

	@action
	switch(): void {
		if (this.popupData.redirectUrl) {
			window.open(this.popupData.redirectUrl, '_blank');
		} else {
			this.popupData.onSwitch();
		}
		this.close();
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name="CheckCircleFill" @size="2xl" />
			<h3>{{{this.formattedText}}}</h3>
			<div class="controls2">
				<div class="controls2__left">
					<SubmitButtonComponent
						@text={{this.stayButtonText}}
						@theme="grey-outlined"
						@onTrigger={{this.close}}
					/>
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent @text={{this.buttonText}} @onTrigger={{this.switch}} />
				</div>
			</div>
		</div>
	</template>
}
